// DiamondEdge Workspace — MLB intelligence workspace with inline game canvas.
// Replaces the routed HomePage + GameDetailPage with a single-view workspace.

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, addDays, subDays } from 'date-fns';
import clsx from 'clsx';
import {
  ChevronLeft, ChevronRight, RefreshCw, Activity, Diamond, Layers,
} from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { useOSStore } from '../../store/useOSStore';
import { gamesApi, advisorApi } from '../../api/client';
import { ModuleWorkspace } from '../../components/workspace/ModuleWorkspace';
import { GameIntelligenceCanvas } from '../../components/workspace/GameIntelligenceCanvas';
import { PulseUplink } from '../../components/workspace/PulseUplink';
import { ParlayRecommendations } from '../../components/Parlay/ParlayRecommendations';
import { DECISION_STATUS_PRIORITY } from '../../utils/recommendations';
import { formatOdds, todayPST } from '../../utils/format';
import { TeamLogo } from '../../components/common/TeamLogo';
import type { GameContext, } from '../../api/client';
import type { BetRecommendation, Bet } from '../../types';

// ── Compact MLB slate card ────────────────────────────────────────────────────

const STATUS_META: Record<string, { label: string; color: string }> = {
  Live:    { label: 'LIVE',    color: 'text-win' },
  Preview: { label: 'Pre',     color: 'text-gray-500' },
  Final:   { label: 'Final',   color: 'text-gray-600' },
};

const AUDIT_BADGE: Record<string, { label: string; cls: string }> = {
  LEAN:    { label: 'LEAN',    cls: 'bg-warn/15 border-warn/30 text-warn' },
  NO_BET:  { label: 'NO BET',  cls: 'bg-surface-3 border-surface-4 text-gray-400' },
  INVALID: { label: 'BLOCKED', cls: 'bg-loss/15 border-loss/30 text-loss' },
};

function DecisionBadge({ rec, auditStatus }: { rec: BetRecommendation | undefined; auditStatus?: string }) {
  if (!rec) {
    if (!auditStatus) return null;
    const m = AUDIT_BADGE[auditStatus];
    if (!m) return null;
    return <span className={clsx('text-[9px] font-bold px-1 py-0.5 rounded border', m.cls)}>{m.label}</span>;
  }
  const status = rec.decisionStatus;
  if (status === 'NO_BET' || status === 'INVALID') return null;
  const meta = {
    STRONG_BET:    { label: 'STRONG',    cls: 'bg-win/15 border-win/40 text-win' },
    QUALIFIED_BET: { label: 'QUALIFIED', cls: 'bg-brand/15 border-brand/40 text-brand-light' },
    LEAN:          { label: 'LEAN',      cls: 'bg-warn/15 border-warn/40 text-warn' },
  }[status];
  if (!meta) return null;
  return (
    <span className={clsx('text-[9px] font-bold px-1 py-0.5 rounded border', meta.cls)}>
      {meta.label}
    </span>
  );
}

function liveBetOutcome(
  bet: Bet,
  awayScore: number | undefined,
  homeScore: number | undefined,
): 'W' | 'T' | 'L' | null {
  if (awayScore === undefined || homeScore === undefined) return null;
  const sel = bet.selection.toLowerCase();
  if (sel.startsWith('over ')) {
    const line = parseFloat(sel.slice(5));
    const total = awayScore + homeScore;
    return total > line ? 'W' : total < line ? 'L' : 'T';
  }
  if (sel.startsWith('under ')) {
    const line = parseFloat(sel.slice(6));
    const total = awayScore + homeScore;
    return total < line ? 'W' : total > line ? 'L' : 'T';
  }
  // Team bet — determine if bet is on away or home by matching last word of team name
  const awayWord = (bet.awayTeam ?? '').split(' ').pop()?.toLowerCase() ?? '';
  const isAway = awayWord.length > 2 && sel.includes(awayWord);
  const myScore = isAway ? awayScore : homeScore;
  const oppScore = isAway ? homeScore : awayScore;
  return myScore > oppScore ? 'W' : myScore < oppScore ? 'L' : 'T';
}

function MLBSlateCard({
  ctx,
  bestRec,
  auditStatus,
  activeBets = [],
  liveScore,
  isActive,
  onClick,
}: {
  ctx: GameContext;
  bestRec?: BetRecommendation;
  auditStatus?: string;
  activeBets?: Bet[];
  liveScore?: { away: number; home: number };
  isActive: boolean;
  onClick: () => void;
}) {
  const { game, odds } = ctx;
  const state = game.status.abstractGameState;
  const statusMeta = STATUS_META[state] ?? { label: state, color: 'text-gray-500' };
  const isLive = state === 'Live';
  const isFinal = state === 'Final';
  const awayScore = liveScore?.away ?? game.awayScore;
  const homeScore = liveScore?.home ?? game.homeScore;

  const OUTCOME_TEXT: Record<string, string> = {
    W: 'text-win',
    L: 'text-loss',
    T: 'text-warn',
  };

  const hasBets = activeBets.length > 0;
  const settled = isLive || isFinal;
  const outcomes = settled
    ? activeBets.map((b) => liveBetOutcome(b, awayScore, homeScore)).filter(Boolean) as ('W' | 'L' | 'T')[]
    : [];
  const wins  = outcomes.filter((o) => o === 'W').length;
  const losses = outcomes.filter((o) => o === 'L').length;
  const ties  = outcomes.filter((o) => o === 'T').length;

  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full text-left px-3 py-2.5 border-b border-surface-4 transition-colors',
        isActive
          ? 'bg-brand/10 border-l-2 border-l-brand'
          : 'hover:bg-surface-1 border-l-2 border-l-transparent',
      )}
    >
      {/* Status + badges row */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          {isLive && <span className="w-1.5 h-1.5 rounded-full bg-win animate-pulse flex-shrink-0" />}
          <span className={clsx('text-[10px] font-semibold', statusMeta.color)}>
            {isLive ? `${game.status.detailedState ?? 'Live'}` : statusMeta.label}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {hasBets && settled && outcomes.length > 0 && (
            activeBets.length === 1 ? (
              <span className={clsx('text-[9px] font-bold px-1 py-0.5 rounded border bg-surface-2 border-surface-4', OUTCOME_TEXT[outcomes[0]])}>
                {isFinal
                  ? (outcomes[0] === 'W' ? 'WON' : outcomes[0] === 'L' ? 'LOST' : 'TIE')
                  : (outcomes[0] === 'W' ? 'WINNING' : outcomes[0] === 'L' ? 'LOSING' : 'TIED')}
              </span>
            ) : (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border bg-surface-2 border-surface-4 font-mono tracking-wide">
                <span className="text-win">{wins}</span>
                <span className="text-gray-600">/</span>
                <span className="text-loss">{losses}</span>
                <span className="text-gray-600">/</span>
                <span className="text-warn">{ties}</span>
              </span>
            )
          )}
          {hasBets && !settled && (
            <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-brand/15 text-brand border-brand/30">
              {activeBets.length > 1 ? `${activeBets.length} BETS` : 'BET'}
            </span>
          )}
          <DecisionBadge rec={bestRec} auditStatus={auditStatus} />
        </div>
      </div>

      {/* Teams */}
      <div className="space-y-0.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 min-w-0 mr-2">
            <TeamLogo abbrev={game.awayTeam.abbreviation} size="xs" />
            <span className="text-xs font-medium text-gray-300 truncate">
              {game.awayTeam.abbreviation}
              <span className="text-gray-500 ml-1 font-normal text-[10px]">{game.awayTeam.name.split(' ').pop()}</span>
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isLive && awayScore !== undefined && (
              <span className="text-xs font-bold text-gray-100 tabular-nums">{awayScore}</span>
            )}
            {odds?.awayMoneyline !== undefined && !isLive && (
              <span className="text-[10px] font-mono text-gray-500 tabular-nums">
                {formatOdds(odds.awayMoneyline)}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 min-w-0 mr-2">
            <TeamLogo abbrev={game.homeTeam.abbreviation} size="xs" />
            <span className="text-xs font-medium text-gray-300 truncate">
              {game.homeTeam.abbreviation}
              <span className="text-gray-500 ml-1 font-normal text-[10px]">{game.homeTeam.name.split(' ').pop()}</span>
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isLive && homeScore !== undefined && (
              <span className="text-xs font-bold text-gray-100 tabular-nums">{homeScore}</span>
            )}
            {odds?.homeMoneyline !== undefined && !isLive && (
              <span className="text-[10px] font-mono text-gray-500 tabular-nums">
                {formatOdds(odds.homeMoneyline)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Edge if recommended */}
      {bestRec && bestRec.edge > 0 && (
        <div className="mt-1.5 flex items-center gap-1">
          <span className="text-[9px] text-gray-600 uppercase tracking-wide">Edge</span>
          <span className="text-[10px] font-mono text-brand-light tabular-nums">
            +{(bestRec.edge * 100).toFixed(1)}%
          </span>
          {bestRec.market && (
            <span className="text-[9px] text-gray-700 truncate ml-auto">{bestRec.market}</span>
          )}
        </div>
      )}
    </button>
  );
}

// ── DiamondEdgeWorkspace ──────────────────────────────────────────────────────

export function DiamondEdgeWorkspace() {
  const {
    games,
    gamesLoading,
    recommendations,
    bets,
    liveGames,
    fetchGames,
    fetchRecommendations,
    fetchBets,
    updateLiveGame,
    selectedDate,
    setSelectedDate,
    parlaySlip,
    parlaySlipOpen,
    setParlaySlipOpen,
  } = useAppStore();

  const { activeGameId, openGameCanvas } = useOSStore();

  const [activeFilter, setActiveFilter] = useState<'all' | 'live' | 'preview' | 'final'>('all');

  useEffect(() => {
    fetchGames(selectedDate);
    fetchRecommendations(selectedDate);
    fetchBets();
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll live data every 10s for live games
  const gamesRef = useRef(games);
  gamesRef.current = games;
  useEffect(() => {
    let cancelled = false;
    const poll = () => {
      gamesRef.current
        .filter((g) => g.game.status.abstractGameState === 'Live')
        .forEach(({ game }) => {
          gamesApi.getLiveGame(game.gamePk)
            .then((data) => { if (!cancelled) updateLiveGame(data); })
            .catch(() => {/* not live */});
        });
    };
    poll();
    const id = setInterval(poll, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const changeDate = (delta: number) => {
    const d = new Date(selectedDate + 'T12:00:00');
    setSelectedDate(format(delta > 0 ? addDays(d, delta) : subDays(d, Math.abs(delta)), 'yyyy-MM-dd'));
  };

  const { data: mlbAuditCandidates = [] } = useQuery({
    queryKey: ['mlb-audit-candidates', selectedDate],
    queryFn: () => advisorApi.getAuditCandidates(selectedDate, undefined, 'mlb'),
    staleTime: 60_000,
  });

  // Build a map of eventPk/gamePk → best non-actionable status for slate badge display
  const statusPriority: Record<string, number> = { LEAN: 0, NO_BET: 1, INVALID: 2 };
  const auditStatusMap = new Map<string, string>();
  for (const r of mlbAuditCandidates) {
    if (r.isActionable) continue;
    const key = r.eventPk ?? (r.gamePk != null ? String(r.gamePk) : null);
    if (!key) continue;
    const existing = auditStatusMap.get(key);
    if (!existing || (statusPriority[r.decisionStatus] ?? 99) < (statusPriority[existing] ?? 99)) {
      auditStatusMap.set(key, r.decisionStatus);
    }
  }

  // Best rec per game
  const recsByGame = new Map<string, BetRecommendation>();
  for (const rec of recommendations) {
    const key = rec.eventPk ?? String(rec.gamePk);
    const existing = recsByGame.get(key);
    if (!existing) { recsByGame.set(key, rec); continue; }
    const ep = DECISION_STATUS_PRIORITY[existing.decisionStatus] ?? 99;
    const rp = DECISION_STATUS_PRIORITY[rec.decisionStatus] ?? 99;
    if (rp < ep || (rp === ep && rec.edge > existing.edge)) recsByGame.set(key, rec);
  }

  const filtered = games.filter((ctx) => {
    const state = ctx.game.status.abstractGameState;
    if (activeFilter === 'live') return state === 'Live';
    if (activeFilter === 'preview') return state === 'Preview';
    if (activeFilter === 'final') return state === 'Final';
    return true;
  });

  const liveCount = games.filter((g) => g.game.status.abstractGameState === 'Live').length;
  const qualifiedCount = recommendations.filter((r) =>
    r.decisionStatus === 'STRONG_BET' || r.decisionStatus === 'QUALIFIED_BET',
  ).length;
  const betNowCount = qualifiedCount; // use qualified as proxy for BET_NOW in uplink
  const isToday = selectedDate === todayPST();

  // ── Slate panel ─────────────────────────────────────────────────────────────

  const slatePanel = (
    <div className="flex flex-col h-full overflow-hidden bg-surface-0">
      {/* Slate header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-surface-4 bg-surface-1/30 flex-shrink-0">
        <Diamond className="w-3.5 h-3.5 text-brand flex-shrink-0" />
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 flex-1">Game Slate</p>
        <span className="text-[10px] text-gray-600 font-mono">{filtered.length} games</span>
        {gamesLoading && <RefreshCw className="w-3 h-3 text-gray-600 animate-spin flex-shrink-0" />}
      </div>

      {/* Date navigation */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-surface-4 flex-shrink-0">
        <button onClick={() => changeDate(-1)} className="p-1 rounded text-gray-500 hover:text-gray-200 hover:bg-surface-2 transition-colors">
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => !isToday && setSelectedDate(todayPST())}
          className={clsx('flex-1 text-center text-[11px] font-medium transition-colors', isToday ? 'text-gray-300' : 'text-brand hover:text-brand-light cursor-pointer')}
        >
          {format(new Date(selectedDate + 'T12:00:00'), 'MMM d, EEE')}
          {isToday && <span className="ml-1 text-[9px] text-brand font-bold">TODAY</span>}
        </button>
        <button onClick={() => changeDate(1)} className="p-1 rounded text-gray-500 hover:text-gray-200 hover:bg-surface-2 transition-colors">
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-1 px-3 py-1.5 border-b border-surface-4 flex-shrink-0 overflow-x-auto">
        {(['all', 'live', 'preview', 'final'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setActiveFilter(f)}
            className={clsx(
              'px-2 py-0.5 text-[10px] font-semibold rounded uppercase tracking-wide whitespace-nowrap transition-colors flex-shrink-0',
              activeFilter === f
                ? 'bg-brand/15 border border-brand/40 text-brand-light'
                : 'text-gray-600 hover:text-gray-300',
            )}
          >
            {f === 'live' && liveCount > 0 ? `Live (${liveCount})` : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Game list */}
      <div className="flex-1 overflow-y-auto">
        {gamesLoading && filtered.length === 0 ? (
          <div className="flex flex-col gap-1.5 p-3">
            {[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-lg bg-surface-2 animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2 text-center px-4">
            <Activity className="w-6 h-6 text-gray-700" />
            <p className="text-xs text-gray-600">No games for this filter.</p>
          </div>
        ) : (
          filtered.map((ctx) => {
            const key = ctx.game.eventPk ?? String(ctx.game.gamePk);
            const activeBets = bets.filter(
              (b) => (b.gamePk != null && b.gamePk === ctx.game.gamePk)
                || b.eventPk === key
            );
            const liveData = liveGames[ctx.game.gamePk];
            const liveScore = (liveData?.awayScore !== undefined && liveData?.homeScore !== undefined)
              ? { away: liveData.awayScore, home: liveData.homeScore }
              : undefined;
            return (
              <MLBSlateCard
                key={ctx.game.gamePk}
                ctx={ctx}
                bestRec={recsByGame.get(key)}
                auditStatus={!recsByGame.has(key) ? auditStatusMap.get(key) : undefined}
                activeBets={activeBets}
                liveScore={liveScore}
                isActive={activeGameId === String(ctx.game.gamePk)}
                onClick={() => openGameCanvas(String(ctx.game.gamePk), 'mlb')}
              />
            );
          })
        )}

        {/* AI-suggested parlays (cross-sport, shown below game slate) */}
        <ParlayRecommendations date={selectedDate} />
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full overflow-hidden bg-surface-0">
      {/* Workspace header */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-surface-4 bg-surface-1/50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-brand/10 border border-brand/20">
            <Diamond className="w-3.5 h-3.5 text-brand" />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-100 leading-tight">DiamondEdge</p>
            <p className="text-[9px] text-gray-500 uppercase tracking-widest leading-tight">MLB Intelligence</p>
          </div>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          {liveCount > 0 && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-md bg-win/10 border border-win/25 text-[10px] font-bold text-win">
              <span className="w-1 h-1 rounded-full bg-win animate-pulse" />
              {liveCount} Live
            </span>
          )}
          {qualifiedCount > 0 && (
            <span className="px-2 py-1 rounded-md bg-brand/10 border border-brand/25 text-[10px] font-semibold text-brand">
              {qualifiedCount} Qualified
            </span>
          )}

          {/* Parlay slip toggle */}
          <button
            onClick={() => setParlaySlipOpen(!parlaySlipOpen)}
            className={clsx(
              'flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold border transition-colors',
              parlaySlip.length > 0
                ? 'bg-brand/15 border-brand/40 text-brand-light hover:bg-brand/25'
                : 'border-surface-4 text-gray-500 hover:text-gray-200 hover:bg-surface-2',
            )}
            title="Parlay slip"
          >
            <Layers className="w-3 h-3" />
            Parlay
            {parlaySlip.length > 0 && (
              <span className="bg-brand text-white rounded-full text-[8px] font-black w-3.5 h-3.5 flex items-center justify-center">
                {parlaySlip.length}
              </span>
            )}
          </button>

          <button
            onClick={() => { fetchGames(selectedDate); fetchRecommendations(selectedDate, true); }}
            disabled={gamesLoading}
            className="flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-gray-200 hover:bg-surface-2 transition-colors disabled:opacity-40"
            title="Refresh"
          >
            <RefreshCw className={clsx('w-3.5 h-3.5', gamesLoading && 'animate-spin')} />
          </button>
        </div>
      </div>

      {/* 3-panel workspace */}
      <ModuleWorkspace
        slatePanel={slatePanel}
        canvas={<GameIntelligenceCanvas />}
        uplinkPanel={
          <PulseUplink
            module="diamondedge"
            recCount={qualifiedCount}
            betNowCount={betNowCount}
          />
        }
      />

    </div>
  );
}
